import { useQuery } from '@tanstack/react-query'
import { Link, type LinkProps } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type DocumentProjectRef,
  type DocumentSummary,
  entityDocumentsQueryOptions,
  isCurrentApproved,
} from '@/entities/document'
import { useSession } from '@/entities/session'
import { entitySlotsQueryOptions, type Slot, type SlotEntityKind } from '@/entities/slot'
import { CreateDocumentDialog } from '@/features/create-document'
import { useAddSlot, useDeleteSlot, useRenameSlot } from '@/features/manage-slots'
import { ApiError } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Chip } from '@/shared/ui/chip'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DateStamp } from '@/shared/ui/date-stamp'
import { Input } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { ZoneNote } from '@/shared/ui/zone-badge'

interface DocumentSlotsProps {
  entityKind: SlotEntityKind
  entityId: string
  /** Роутинг остаётся у страницы: как из id документа получить ссылку на него. */
  docLink: (docId: string) => LinkProps
  /** company-only: единый список — плюс документы всех сделок (scope=with-projects). */
  withProjects?: boolean
  /** Ссылка на проектный документ (роут страницы проекта). */
  projectDocLink?: (docId: string, project: DocumentProjectRef) => LinkProps
  /** Ссылка на страницу проекта (чип названия). */
  projectLink?: (project: DocumentProjectRef) => LinkProps
}

// Документарная часть сущности: каждый слот — своя карточка, документы —
// строки внутри. Слоты рендерятся все, даже пустые (dashed «Добавить документ»).
// Сотруднику — inline-CRUD слотов; заказчик конфиг-аффордансов не видит.
// withProjects: следом — группа «Документы проектов» (строки с чипом проекта).
export function DocumentSlots({
  entityKind,
  entityId,
  docLink,
  withProjects,
  projectDocLink,
  projectLink,
}: DocumentSlotsProps) {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const accountId = session?.id
  // Свой слот заказчика: создан им (kind=customer + совпадение account id).
  const isOwnSlot = (slot: Slot) =>
    !isEmployee && slot.created_by.kind === 'customer' && slot.created_by.id === accountId

  const slotsQ = useQuery(entitySlotsQueryOptions(entityKind, entityId))
  const docsQ = useQuery(
    entityDocumentsQueryOptions(entityKind, entityId, withProjects ? 'with-projects' : undefined),
  )
  const [createSlotId, setCreateSlotId] = useState<string | null>(null)

  if (slotsQ.isPending || docsQ.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (slotsQ.error || docsQ.error) {
    return <StatusScreen title="Не удалось загрузить документы" message="Обновите страницу." />
  }

  const slots = slotsQ.data
  const documents = docsQ.data
  const docsOf = (slotId: string) =>
    documents.filter((d) => d.slot_id === slotId).sort((a, b) => a.order_in_slot - b.order_in_slot)
  // Проектные строки листинга with-projects: их слоты — чужие (слоты проектов).
  const projectDocs = documents.filter(
    (d): d is DocumentSummary & { project: DocumentProjectRef } => d.project != null,
  )

  return (
    <div className="flex flex-col gap-4">
      {/* Время загрузки версии — момент, и он повторяется в каждой строке
          каждого слота: зона подписывается один раз на весь список. */}
      <ZoneNote what="Время загрузки" />
      {slots.map((slot) => (
        <SlotCard
          key={slot.id}
          slot={slot}
          docs={docsOf(slot.id)}
          entityKind={entityKind}
          entityId={entityId}
          isEmployee={isEmployee}
          isOwn={isOwnSlot(slot)}
          docLink={docLink}
          onAddDocument={() => setCreateSlotId(slot.id)}
        />
      ))}

      <AddSlotAffordance entityKind={entityKind} entityId={entityId} isEmployee={isEmployee} />

      {withProjects && projectDocs.length > 0 && projectDocLink && projectLink && (
        <ProjectDocsSection
          docs={projectDocs}
          isEmployee={isEmployee}
          projectDocLink={projectDocLink}
          projectLink={projectLink}
        />
      )}

      {createSlotId && (
        <CreateDocumentDialog
          entityKind={entityKind}
          entityId={entityId}
          defaultSlotId={createSlotId}
          open
          onClose={() => setCreateSlotId(null)}
        />
      )}
    </div>
  )
}

type ProjectDoc = DocumentSummary & { project: DocumentProjectRef }

// Группировка проектных документов: проект → слот, порядок появления сохраняется.
function groupProjectDocs(docs: ProjectDoc[]) {
  const byProject = new Map<
    string,
    {
      project: DocumentProjectRef
      slots: Map<string, { id: string; name: string; docs: ProjectDoc[] }>
    }
  >()
  for (const doc of docs) {
    let dg = byProject.get(doc.project.id)
    if (!dg) {
      dg = { project: doc.project, slots: new Map() }
      byProject.set(doc.project.id, dg)
    }
    let sg = dg.slots.get(doc.slot.id)
    if (!sg) {
      sg = { id: doc.slot.id, name: doc.slot.name, docs: [] }
      dg.slots.set(doc.slot.id, sg)
    }
    sg.docs.push(doc)
  }
  return [...byProject.values()].map((dg) => ({
    project: dg.project,
    slots: [...dg.slots.values()],
  }))
}

// Документы по проектам компании: сгруппированы проект → слот. Заголовок проекта
// ведёт на её страницу, под ним — слоты с названиями и их документы.
function ProjectDocsSection({
  docs,
  isEmployee,
  projectDocLink,
  projectLink,
}: {
  docs: ProjectDoc[]
  isEmployee: boolean
  projectDocLink: (docId: string, project: DocumentProjectRef) => LinkProps
  projectLink: (project: DocumentProjectRef) => LinkProps
}) {
  const projectGroups = groupProjectDocs(docs)
  return (
    <section className="flex flex-col gap-4 rounded-card bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset">
      <div className="flex items-center gap-2">
        <h2 className="label text-text-secondary">Документы по проектам</h2>
        <span className="mono-num text-text-tertiary text-xs">{docs.length}</span>
      </div>
      {projectGroups.map((group) => (
        <div key={group.project.id} className="flex flex-col gap-2.5">
          <Link
            {...projectLink(group.project)}
            title={`Проект «${group.project.title}»`}
            className="inline-flex max-w-full items-center gap-1.5 self-start truncate rounded-control bg-bg-raised px-2.5 py-1 font-sans font-medium text-sm text-text-primary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
          >
            <Icon name="columns" size={14} className="shrink-0 text-text-tertiary" />
            {group.project.title}
          </Link>
          {group.slots.map((sg) => (
            <div key={sg.id} className="flex flex-col gap-1.5 pl-1">
              <div className="flex items-center gap-2">
                <span className="label text-text-tertiary">{sg.name}</span>
                <span className="mono-num text-text-tertiary text-xs">{sg.docs.length}</span>
              </div>
              <ul className="flex flex-col gap-2">
                {sg.docs.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center gap-3 rounded-control bg-bg-elevated px-4 py-3 ring-1 ring-border-subtle ring-inset"
                  >
                    <Icon name="document" size={18} className="shrink-0 text-text-tertiary" />
                    <span className="min-w-0 flex-1">
                      <Link
                        {...projectDocLink(doc.id, doc.project)}
                        className="block truncate font-medium text-sm text-text-primary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                      >
                        {doc.title}
                      </Link>
                      <span className="mono-num block text-text-tertiary text-xs">
                        v{doc.current_version.number} ·{' '}
                        <DateStamp value={doc.current_version.uploaded_at} />
                      </span>
                    </span>
                    <DocStatus doc={doc} isEmployee={isEmployee} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </section>
  )
}

interface SlotCardProps {
  slot: Slot
  docs: DocumentSummary[]
  entityKind: SlotEntityKind
  entityId: string
  isEmployee: boolean
  /** Слот создан текущим заказчиком — ему доступны rename/delete своего слота. */
  isOwn: boolean
  docLink: (docId: string) => LinkProps
  onAddDocument: () => void
}

function SlotCard({
  slot,
  docs,
  entityKind,
  entityId,
  isEmployee,
  isOwn,
  docLink,
  onAddDocument,
}: SlotCardProps) {
  const renameM = useRenameSlot(entityKind, entityId)
  const deleteM = useDeleteSlot(entityKind, entityId)
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(slot.name)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const canUpload = isEmployee || slot.customer_can_upload
  // Управлять слотом (rename/delete) может сотрудник или владелец-заказчик.
  const canManage = isEmployee || isOwn

  function saveRename() {
    const name = draft.trim()
    setRenaming(false)
    if (!name || name === slot.name) return
    renameM.mutate(
      { id: slot.id, name },
      { onError: () => toast.error('Не удалось переименовать слот') },
    )
  }

  // Заказчик удаляет свой слот каскадом (сервер снесёт и документы) — всегда
  // доступно. Сотрудник — только пустой слот (непустой → 409 SLOT_NOT_EMPTY):
  // кнопка задизейблена с подсказкой.
  const cascades = isOwn
  const canDelete = isEmployee ? docs.length === 0 : true

  return (
    <section className="flex flex-col gap-3 rounded-card bg-bg-surface p-4 ring-1 ring-inset ring-border-subtle">
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <Input
            aria-label="Название слота"
            value={draft}
            // Инпут появляется по явному действию (карандаш) — фокус ожидаем.
            ref={(el) => el?.focus()}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename()
              if (e.key === 'Escape') {
                setDraft(slot.name)
                setRenaming(false)
              }
            }}
            className="w-56 px-3 pt-1.5 pb-2 text-sm"
          />
        ) : (
          // h2, не h3: слот — секция верхнего уровня внутри таба (после h1
          // страницы). h1→h3 ломает порядок заголовков (axe heading-order).
          <h2 className="label text-text-secondary">{slot.name}</h2>
        )}
        <span className="mono-num text-xs text-text-tertiary">{docs.length}</span>
        {slot.customer_can_upload && (
          <Chip tone="info" variant="soft">
            заказчик может загружать
          </Chip>
        )}

        <span className="ml-auto flex items-center gap-1">
          {canUpload && docs.length > 0 && (
            <button
              type="button"
              onClick={onAddDocument}
              className="inline-flex items-center gap-1 rounded-control px-2 py-1 text-xs text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
            >
              <Icon name="plus" size={14} />
              документ
            </button>
          )}
          {canManage && !renaming && (
            <button
              type="button"
              aria-label={`Переименовать слот «${slot.name}»`}
              onClick={() => {
                setDraft(slot.name)
                setRenaming(true)
              }}
              className="rounded-control p-1.5 text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
            >
              <Icon name="pencil" size={14} />
            </button>
          )}
          {canManage && (
            <span
              className="inline-flex"
              title={canDelete ? undefined : 'Сначала удалите документы слота'}
            >
              <button
                type="button"
                aria-label={
                  canDelete
                    ? `Удалить слот «${slot.name}»`
                    : `Слот «${slot.name}» нельзя удалить: в нём есть документы`
                }
                disabled={deleteM.isPending || !canDelete}
                onClick={() => setConfirmDelete(true)}
                className="rounded-control p-1.5 text-text-tertiary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon name="close" size={14} />
              </button>
            </span>
          )}
        </span>
      </div>

      {docs.length === 0 ? (
        canUpload ? (
          <button
            type="button"
            onClick={onAddDocument}
            className="flex items-center justify-center gap-2 rounded-control border border-border-default border-dashed px-4 py-3 text-sm text-text-secondary transition-colors hover:border-border-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
          >
            <Icon name="plus" size={16} />
            Добавить документ
          </button>
        ) : (
          <p className="text-sm text-text-tertiary">Нет документов</p>
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {docs.map((doc) => (
            <li key={doc.id}>
              <Link
                {...docLink(doc.id)}
                className="flex items-center gap-3 rounded-control bg-bg-elevated px-4 py-3 ring-1 ring-border-subtle ring-inset transition-colors hover:ring-border-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                <Icon name="document" size={18} className="shrink-0 text-text-tertiary" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-text-primary">
                    {doc.title}
                  </span>
                  <span className="mono-num block text-xs text-text-tertiary">
                    v{doc.current_version.number} ·{' '}
                    <DateStamp value={doc.current_version.uploaded_at} />
                  </span>
                </span>
                <DocStatus doc={doc} isEmployee={isEmployee} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={`Удалить слот «${slot.name}»?`}
        confirmLabel="Удалить"
        pending={deleteM.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteM.mutate(slot.id, {
            onSuccess: () => setConfirmDelete(false),
            onError: (e) =>
              toast.error(
                e instanceof ApiError && e.code === 'SLOT_NOT_EMPTY'
                  ? 'Сначала удалите документы слота'
                  : 'Не удалось удалить слот',
              ),
          })
        }
      >
        <p className="text-sm text-text-secondary">
          {cascades && docs.length > 0
            ? `Слот будет удалён вместе с документами внутри (${docs.length}) без возможности восстановления.`
            : 'Слот будет удалён без возможности восстановления.'}
        </p>
      </ConfirmDialog>
    </section>
  )
}

function AddSlotAffordance({
  entityKind,
  entityId,
  isEmployee,
}: {
  entityKind: SlotEntityKind
  entityId: string
  isEmployee: boolean
}) {
  const addM = useAddSlot(entityKind, entityId)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [customerCanUpload, setCustomerCanUpload] = useState(false)

  function submit() {
    const trimmed = name.trim()
    if (!trimmed) return
    // Флаг загрузки задаёт только сотрудник; у слота заказчика сервер ставит true.
    addM.mutate(
      { name: trimmed, ...(isEmployee ? { customer_can_upload: customerCanUpload } : {}) },
      {
        onSuccess: () => {
          setOpen(false)
          setName('')
          setCustomerCanUpload(false)
        },
        onError: () => toast.error('Не удалось добавить слот'),
      },
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-2 rounded-card border border-border-default border-dashed px-4 py-3 text-sm text-text-secondary transition-colors hover:border-border-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
      >
        <Icon name="plus" size={16} />
        Слот
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-card bg-bg-surface p-4 ring-1 ring-inset ring-border-subtle">
      <Input
        aria-label="Название нового слота"
        value={name}
        // Форма появляется по явному действию («+ Слот») — фокус ожидаем.
        ref={(el) => el?.focus()}
        placeholder="Название слота"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isEmployee && (
          <Checkbox
            checked={customerCanUpload}
            onChange={(e) => setCustomerCanUpload(e.target.checked)}
            label="заказчик может загружать"
            labelClassName="text-xs text-text-secondary"
          />
        )}
        <div className="ml-auto flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={addM.isPending}
          >
            Отмена
          </Button>
          <Button size="sm" onClick={submit} disabled={addM.isPending || !name.trim()}>
            Добавить
          </Button>
        </div>
      </div>
    </div>
  )
}

function DocStatus({ doc, isEmployee }: { doc: DocumentSummary; isEmployee: boolean }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {doc.uploaded_by_customer && (
        <Chip tone="info" variant="soft">
          от заказчика
        </Chip>
      )}
      {isCurrentApproved(doc) && <StatusBadge status="approved" />}
      {isEmployee && doc.draft && <StatusBadge status="draft" />}
      {isEmployee && !doc.draft && doc.visible_to_customer === false && (
        <Chip tone="neutral" variant="soft">
          скрыт
        </Chip>
      )}
    </span>
  )
}
